import type { GanttTask } from '../types/gantt';

export type TaskLegalStatus = 'potential_restriction' | 'date_conflict';

export type TaskLegalMeta = {
	status: TaskLegalStatus;
	reason: string;
};

function normalizeCountryCode(countryCode?: string | null): string {
	return String(countryCode || '').trim().toUpperCase();
}

function toDateOnly(value?: string | null): Date | null {
	if (!value) return null;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
}

export function isGermanySeasonalBanDate(date: Date): boolean {
	const year = date.getFullYear();
	const start = new Date(year, 2, 1);
	const end = new Date(year, 8, 30, 23, 59, 59, 999);
	return date >= start && date <= end;
}

export function getTaskLegalMeta(
	task: Pick<GanttTask, 'legalSeasonalBanCandidate' | 'legalSeasonalBanReason' | 'startDate' | 'endDate'>,
	countryCode?: string | null
): TaskLegalMeta | null {
	if (!task.legalSeasonalBanCandidate) return null;

	const baseReason =
		task.legalSeasonalBanReason?.trim() ||
		'This task may include major hedge, shrub, or tree cutting.';
	if (normalizeCountryCode(countryCode) !== 'DE') return null;

	const start = toDateOnly(task.startDate);
	const end = toDateOnly(task.endDate) || start;
	if (!start || !end) {
		return {
			status: 'potential_restriction',
			reason: `${baseReason} In Germany, this kind of work may need scheduling outside the Mar 1-Sep 30 protection period.`
		};
	}

	const cursor = new Date(start);
	while (cursor <= end) {
		if (isGermanySeasonalBanDate(cursor)) {
			return {
				status: 'date_conflict',
				reason: `${baseReason} It is currently scheduled during Germany's Mar 1-Sep 30 protection period.`
			};
		}
		cursor.setDate(cursor.getDate() + 1);
	}

	return null;
}
